export const NOVELTY_FILE_ID_PREFIX = 'novelty-analysis:'
export const NOVELTY_TASK_STORAGE_PREFIX = 'novelty-analysis-task:'

const ACTIVE_TASK_STATUS_CODES = new Set(['PENDING', 'RUNNING'])
const FILE_ITEM_PREFIX = 'file:'

const toTrimmedString = (value) => String(value || '').trim()

const hashJavaString = (value = '') => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) | 0
  }
  return (hash >>> 0).toString(16)
}

export const buildNoveltySourceVersion = (featureValue = '') => {
  const normalizedFeatureValue = toTrimmedString(featureValue)
  return normalizedFeatureValue ? hashJavaString(normalizedFeatureValue) : ''
}

export const isNoveltySourceStale = ({
  hasFeatureContent = false,
  hasResultRows = false,
  resultSourceVersion = '',
  currentSourceVersion = '',
} = {}) => {
  return Boolean(
    hasFeatureContent &&
    hasResultRows &&
    resultSourceVersion &&
    currentSourceVersion &&
    resultSourceVersion !== currentSourceVersion
  )
}

const normalizeFileToken = (value, fallback = 'file') => {
  return encodeURIComponent(String(value || fallback))
}

const buildGeneratedRuntimeFileId = (fileId = '') => {
  const baseToken = toTrimmedString(fileId) || 'generated-0'
  return `${FILE_ITEM_PREFIX}generated:${normalizeFileToken(baseToken, 'generated-0')}`
}

const normalizeFeatureCodes = (value = []) => {
  if (!Array.isArray(value)) {
    return []
  }

  const result = []
  const usedCodes = new Set()

  value.forEach((code) => {
    const normalizedCode = toTrimmedString(code)
    if (!normalizedCode || usedCodes.has(normalizedCode)) {
      return
    }
    usedCodes.add(normalizedCode)
    result.push(normalizedCode)
  })

  return result
}

const matchesByString = (left, right) => {
  const normalizedLeft = toTrimmedString(left)
  const normalizedRight = toTrimmedString(right)
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight)
}

export const createNoveltyDocumentFileId = (pn = '') => {
  const normalizedPn = toTrimmedString(pn)
  return normalizedPn ? `${NOVELTY_FILE_ID_PREFIX}${normalizedPn}` : ''
}

export const getNoveltyGeneratedRuntimeItemId = (document = {}) => {
  const fileId = createNoveltyDocumentFileId(document?.pn)
  if (!fileId) {
    return ''
  }
  return buildGeneratedRuntimeFileId(fileId)
}

export const buildNoveltyAttachment = (document = {}) => {
  return {
    id: createNoveltyDocumentFileId(document?.pn),
    refCode: toTrimmedString(document?.refCode),
    pn: toTrimmedString(document?.pn),
    fileName: toTrimmedString(document?.fileName),
    filePath: toTrimmedString(document?.filePath),
    fileUrl: toTrimmedString(document?.fileUrl),
    previewPath: toTrimmedString(document?.previewPath),
    viewerData: document?.viewerData || null,
  }
}

export const findNoveltyWorkspaceFileItem = (workspaceFileItems = [], document = {}) => {
  if (!Array.isArray(workspaceFileItems) || !workspaceFileItems.length) {
    return null
  }

  const noveltyFileId = createNoveltyDocumentFileId(document?.pn)
  const runtimeItemId = getNoveltyGeneratedRuntimeItemId(document)
  const pn = toTrimmedString(document?.pn)
  const refCode = toTrimmedString(document?.refCode)
  const filePath = toTrimmedString(document?.filePath)
  const fileUrl = toTrimmedString(document?.fileUrl)

  return workspaceFileItems.find(item => (
    matchesByString(item?.id, runtimeItemId) ||
    matchesByString(item?.fileMeta?.id, noveltyFileId) ||
    matchesByString(item?.fileMeta?.pn, pn) ||
    matchesByString(item?.fileMeta?.refCode, refCode) ||
    matchesByString(item?.filePath, filePath) ||
    matchesByString(item?.fileUrl, fileUrl)
  )) || null
}

export const buildNoveltyDocumentViewTarget = (projectId = '', document = {}) => {
  const previewPath = toTrimmedString(document?.previewPath)
  if (previewPath) {
    return previewPath
  }

  const fileUrl = toTrimmedString(document?.fileUrl)
  if (fileUrl) {
    return fileUrl
  }

  if (!document?.viewerData?.docResult) {
    return ''
  }

  const runtimeItemId = getNoveltyGeneratedRuntimeItemId(document)
  const normalizedProjectId = toTrimmedString(projectId)
  if (!normalizedProjectId || !runtimeItemId) {
    return ''
  }

  return `/writing/fullscreen?id=${encodeURIComponent(normalizedProjectId)}&itemId=${encodeURIComponent(runtimeItemId)}`
}

export const createNoveltyTaskStorageKey = (projectId = '') => {
  const normalizedProjectId = toTrimmedString(projectId)
  return normalizedProjectId ? `${NOVELTY_TASK_STORAGE_PREFIX}${normalizedProjectId}` : ''
}

export const normalizeNoveltyTaskStatus = (value = {}) => {
  return {
    taskId: toTrimmedString(value?.taskId),
    statusCode: toTrimmedString(value?.statusCode),
    stageCode: toTrimmedString(value?.stageCode),
    stageText: toTrimmedString(value?.stageText),
    showStage: value?.showStage !== false,
    progressPercent: Number.isFinite(Number(value?.progressPercent)) ? Number(value.progressPercent) : 0,
    lastVisibleStageText: toTrimmedString(value?.lastVisibleStageText),
    lastVisibleProgressPercent: Number.isFinite(Number(value?.lastVisibleProgressPercent))
      ? Number(value.lastVisibleProgressPercent)
      : 0,
    errorMessage: toTrimmedString(value?.errorMessage),
    selectedFeatureCodes: normalizeFeatureCodes(value?.selectedFeatureCodes),
  }
}

export const shouldPersistNoveltyTaskStatus = (value = {}) => {
  const normalized = normalizeNoveltyTaskStatus(value)
  return Boolean(normalized.taskId && ACTIVE_TASK_STATUS_CODES.has(normalized.statusCode))
}

const getFallbackStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }
  return window.localStorage || window.sessionStorage || null
}

export const getNoveltyTaskStorage = () => getFallbackStorage()

export const readNoveltyTaskState = (storage, projectId = '') => {
  const storageKey = createNoveltyTaskStorageKey(projectId)
  if (!storageKey || !storage?.getItem) {
    return null
  }

  try {
    const rawValue = storage.getItem(storageKey)
    if (!rawValue) {
      return null
    }
    const parsedValue = JSON.parse(rawValue)
    const normalized = normalizeNoveltyTaskStatus(parsedValue)
    return shouldPersistNoveltyTaskStatus(normalized) ? normalized : null
  } catch (error) {
    return null
  }
}

export const writeNoveltyTaskState = (storage, projectId = '', value = {}) => {
  const storageKey = createNoveltyTaskStorageKey(projectId)
  if (!storageKey || !storage?.setItem) {
    return
  }

  const normalized = normalizeNoveltyTaskStatus(value)
  if (!shouldPersistNoveltyTaskStatus(normalized)) {
    clearNoveltyTaskState(storage, projectId)
    return
  }

  storage.setItem(storageKey, JSON.stringify(normalized))
}

export const clearNoveltyTaskState = (storage, projectId = '') => {
  const storageKey = createNoveltyTaskStorageKey(projectId)
  if (!storageKey || !storage?.removeItem) {
    return
  }
  storage.removeItem(storageKey)
}
